import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { PROPERTY_PAGE_CSS } from './propertyPageStyles';
import PropertyPhotosBlock, { PropertyPhoto } from './PropertyPhotosBlock';
import { expiryHint, propertyDisplayTags, formatLegalOwner } from './propertyFormShared';

const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-GB') : '—';
const fmtMoney = (n: number) => '£' + Number(n || 0).toLocaleString();
const labelize = (s: string) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function Spec({ label, value, muted }: { label: string; value?: string | number | null; muted?: boolean }) {
  return (
    <div className="pp-spec-item">
      <label>{label}</label>
      <span className={!value && value !== 0 ? 'muted' : ''}>{value ?? '—'}</span>
    </div>
  );
}

function complianceBadge(dateStr?: string | null) {
  if (!dateStr) return null;
  const h = expiryHint(String(dateStr).split('T')[0]);
  if (!h) return null;
  const cls = h.color === '#dc2626' ? 'pp-badge-red' : h.color === '#d97706' ? 'pp-badge-amber' : h.color === '#16a34a' ? 'pp-badge-green' : 'pp-badge-grey';
  return <span className={`pp-badge ${cls}`} style={{ marginLeft: 6 }}>{h.text}</span>;
}

export default function PropertyViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/landlord/properties/${id}/summary`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Not found');
      setDetail(await r.json());
    } catch {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => { load(); }, [load]);

  const uploadPhotos = async (files: FileList) => {
    if (!token || !id || !files.length) return;
    setUploadingPhotos(true);
    const fd = new FormData();
    Array.from(files).slice(0, 5).forEach(f => fd.append('photos', f));
    try {
      await fetch(`${API_URL}/landlord/properties/${id}/photos`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      await load();
    } finally { setUploadingPhotos(false); }
  };

  const deletePhoto = async (photoId: number) => {
    if (!token || !id) return;
    await fetch(`${API_URL}/landlord/properties/${id}/photos/${photoId}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    load();
  };

  const archiveProperty = async () => {
    if (!token || !id || !window.confirm('Archive this property? All data is kept — you can restore from the portfolio.')) return;
    await fetch(`${API_URL}/landlord/properties/${id}/archive`, {
      method: 'PATCH', headers: { Authorization: `Bearer ${token}` },
    });
    navigate('/landlord/properties');
  };

  const payRent = async (rentId: number) => {
    if (!token) return;
    await fetch(`${API_URL}/landlord/rent/${rentId}/pay`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  if (loading) {
    return <div className="pp-page"><style>{PROPERTY_PAGE_CSS}</style><div style={{ textAlign: 'center', padding: 80, color: '#9ca3af' }}>Loading property…</div></div>;
  }
  if (!detail?.property) {
    return <div className="pp-page"><style>{PROPERTY_PAGE_CSS}</style><div style={{ textAlign: 'center', padding: 80 }}><p>Property not found</p><button className="pp-btn pp-btn-outline" onClick={() => navigate('/landlord/properties')}>Back</button></div></div>;
  }

  const p = detail.property;
  const photos: PropertyPhoto[] = detail.photos || [];
  const hero = photos[heroIdx]?.url;
  const tags = propertyDisplayTags(p);

  const statusLabel: Record<string, string> = {
    to_let: 'To Let', ready_to_rent: 'Ready to Rent', available: 'Vacant', under_offer: 'Let Agreed',
    let: 'Let', maintenance: 'Maintenance',
  };

  return (
    <div className="pp-page">
      <style>{PROPERTY_PAGE_CSS}</style>

      <header className="pp-top">
        <div className="pp-top-inner">
          <div>
            <button type="button" className="pp-back" onClick={() => navigate('/landlord/properties')}>← Portfolio</button>
            <h1 className="pp-title">{p.address}</h1>
            <div className="pp-meta">
              <span>{p.postcode}</span>
              <span>·</span>
              <span>{p.property_type}</span>
              <span>·</span>
              <span className={`pp-badge ${p.ready_to_rent ? 'pp-badge-green' : 'pp-badge-grey'}`}>
                {statusLabel[p.status] || labelize(p.status)}
              </span>
              <span>·</span>
              <span className="pp-rent">{fmtMoney(p.monthly_rent)}/mo</span>
            </div>
          </div>
          <div className="pp-actions">
            <button type="button" className="pp-btn pp-btn-primary" onClick={() => navigate(`/landlord/properties/${id}/edit`)}>Edit Property</button>
            <button type="button" className="pp-btn pp-btn-danger" onClick={archiveProperty}>Archive</button>
          </div>
        </div>
      </header>

      <div className="pp-body">
        {/* Photo gallery */}
        <div className="pp-gallery">
          <div className="pp-gallery-main">
            {hero ? <img src={hero} alt="" /> : (
              <div className="pp-gallery-placeholder">
                <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280' }}>No photos</span>
                <button type="button" className="pp-btn pp-btn-outline" style={{ marginTop: 8 }} onClick={() => navigate(`/landlord/properties/${id}/edit`)}>Add photos in Edit</button>
              </div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="pp-gallery-strip">
              {photos.map((ph, i) => (
                <button key={ph.id} type="button" className={`pp-gallery-thumb${i === heroIdx ? ' active' : ''}`} onClick={() => setHeroIdx(i)}>
                  <img src={ph.url} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="pp-grid-2">
          <div>
            <section className="pp-section">
              <h2 className="pp-section-title">Property Overview</h2>
              <div className="pp-spec-grid">
                <Spec label="Unit Type" value={labelize(p.unit_type)} />
                <Spec label="Rooms" value={p.bedrooms} />
                <Spec label="Bathrooms" value={p.bathrooms} />
                <Spec label="Furnished" value={labelize(p.furnished)} />
                <Spec label="Bills Included" value={labelize(p.bills_included)} />
                <Spec label="Total Area" value={p.total_area_sqm ? `${p.total_area_sqm} m²` : null} />
                <Spec label="Price per m²" value={p.price_per_sqm ? fmtMoney(p.price_per_sqm) : null} />
                <Spec label="Floor" value={p.floor != null ? String(p.floor) : null} />
                <Spec label="Total Floors" value={p.total_floors != null ? String(p.total_floors) : null} />
                <Spec label="Parking" value={labelize(p.parking)} />
                <Spec label="Ground Floor" value={p.ground_floor === 'yes' ? 'Yes' : 'No'} />
                <Spec label="Wheelchair Access" value={p.wheelchair_accessible === 'yes' ? 'Yes' : 'No'} />
                <Spec label="Lift" value={p.has_lift === 'yes' ? 'Yes' : 'No'} />
              </div>
              {tags.length > 0 && (
                <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {tags.map(t => (
                    <span key={t} className="pp-badge pp-badge-grey">{t}</span>
                  ))}
                </div>
              )}
            </section>

            <section className="pp-section">
              <h2 className="pp-section-title">Legal & Compliance</h2>
              <div className="pp-spec-grid">
                <Spec label="Legal Landlord" value={formatLegalOwner(p.legal_owner_title, p.legal_owner_name) || 'Not set — add in Edit'} />
                <Spec label="EPC Rating" value={p.epc_rating || 'Not set'} />
                <Spec label="Council Tax" value={p.council_tax_band ? `Band ${p.council_tax_band}` : null} />
                <Spec label="Tenure" value={labelize(p.tenure)} />
                {p.hmo_license && <Spec label="HMO Licence" value={p.hmo_license} />}
              </div>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  ['Gas Safety (CP12)', p.gas_safety_expiry],
                  ['EICR (Electrical)', p.eicr_expiry],
                  ['Landlord Insurance', p.insurance_expiry],
                ].map(([label, date]) => (
                  <div key={String(label)} style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong style={{ minWidth: 160 }}>{label}</strong>
                    <span>{date ? fmt(String(date)) : '—'}</span>
                    {complianceBadge(date as string)}
                  </div>
                ))}
              </div>
              {p.ready_to_rent && (
                <div style={{ marginTop: 12 }}><span className="pp-badge pp-badge-green">Ready to Rent</span></div>
              )}
            </section>

            {(p.description || p.shared_spaces) && (
              <section className="pp-section">
                <h2 className="pp-section-title">Description & Notes</h2>
                {p.description && <div style={{ marginBottom: 12 }}><div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 4 }}>DESCRIPTION</div><div className="pp-text-block">{p.description}</div></div>}
                {p.shared_spaces && <div><div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 4 }}>SHARED SPACES & ACCESS</div><div className="pp-text-block">{p.shared_spaces}</div></div>}
              </section>
            )}

            <section className="pp-section">
              <h2 className="pp-section-title">Photos</h2>
              <PropertyPhotosBlock
                photos={photos}
                uploading={uploadingPhotos}
                onUpload={uploadPhotos}
                onDelete={deletePhoto}
                hint="Add up to 5 photos for listings, viewings, and tenant onboarding."
              />
            </section>
          </div>

          <div>
            <section className="pp-section">
              <h2 className="pp-section-title">Rent This Month</h2>
              {detail.rent ? (
                <div className="pp-row" style={{ background: detail.rent.paid ? '#f0fdf4' : '#fef2f2' }}>
                  <div>
                    <div className="pp-row-title" style={{ color: detail.rent.paid ? '#166534' : '#991b1b' }}>
                      {detail.rent.paid ? 'Paid' : 'Unpaid'} — {fmtMoney(detail.rent.amount_due)}
                    </div>
                    <div className="pp-row-sub">{detail.rent.tenant_name}</div>
                  </div>
                  {!detail.rent.paid && (
                    <button type="button" className="pp-btn pp-btn-primary" style={{ padding: '7px 14px', fontSize: 12 }} onClick={() => payRent(detail.rent.id)}>Mark Paid</button>
                  )}
                </div>
              ) : <div className="pp-empty">No rent record this month</div>}
            </section>

            <section className="pp-section">
              <h2 className="pp-section-title">Rooms ({(detail.rooms || []).length})</h2>
              {(detail.rooms || []).length === 0 ? (
                <div className="pp-empty">No rooms defined — <button type="button" className="pp-btn pp-btn-outline" style={{ padding: '4px 10px', fontSize: 11, marginLeft: 4 }} onClick={() => navigate(`/landlord/properties/${id}/edit`)}>Add in Edit</button></div>
              ) : (detail.rooms || []).map((rm: any) => (
                <div key={rm.id} className="pp-row">
                  <div>
                    <div className="pp-row-title">{rm.room_name}{rm.room_number ? ` · ${rm.room_number}` : ''}</div>
                    <div className="pp-row-sub">{[rm.size_sqm && `${rm.size_sqm}m²`, rm.size_sqft && `${rm.size_sqft}ft²`, rm.size_description].filter(Boolean).join(' · ')}</div>
                  </div>
                </div>
              ))}
            </section>

            <section className="pp-section">
              <h2 className="pp-section-title">Tenants ({detail.tenants?.length || 0})</h2>
              {(detail.tenants || []).length === 0 ? <div className="pp-empty">No tenants linked</div> : (detail.tenants || []).map((t: any) => (
                <div key={t.id} className="pp-row" style={{ cursor: 'pointer' }} onClick={() => navigate(`/landlord/tenants/${t.id}`)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && navigate(`/landlord/tenants/${t.id}`)}>
                  <div>
                    <div className="pp-row-title">{t.full_name}</div>
                    <div className="pp-row-sub">{t.email}{t.phone ? ` · ${t.phone}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`pp-badge ${String(t.computed_status || t.status) === 'active' ? 'pp-badge-green' : 'pp-badge-grey'}`}>
                      {String(t.computed_status || t.status) === 'active' ? 'Active' : 'Former'}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>View →</span>
                  </div>
                </div>
              ))}
            </section>

            <section className="pp-section">
              <h2 className="pp-section-title">Tenancies ({detail.tenancies?.length || 0})</h2>
              {(detail.tenancies || []).length === 0 ? <div className="pp-empty">No tenancies</div> : (detail.tenancies || []).slice(0, 5).map((l: any) => (
                <div key={l.id} className="pp-row" style={{ cursor: 'pointer' }} onClick={() => navigate(`/landlord/leases/${l.id}`)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && navigate(`/landlord/leases/${l.id}`)}>
                  <div>
                    <div className="pp-row-title">{l.tenant_name}</div>
                    <div className="pp-row-sub">{fmt(l.start_date)} → {fmt(l.end_date)} · {fmtMoney(l.monthly_rent)}/mo</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>View →</span>
                </div>
              ))}
              <Link to="/landlord/leases" style={{ fontSize: 12, color: '#16a34a', fontWeight: 700, textDecoration: 'none' }}>View all leases →</Link>
            </section>

            <section className="pp-section">
              <h2 className="pp-section-title">Maintenance ({detail.maintenance?.length || 0})</h2>
              {(detail.maintenance || []).length === 0 ? <div className="pp-empty">No jobs</div> : (detail.maintenance || []).slice(0, 4).map((m: any) => (
                <div key={m.id} className="pp-row">
                  <div>
                    <div className="pp-row-title">{m.description}</div>
                    <div className="pp-row-sub">{fmt(m.reported_date)} · {labelize(m.status)}</div>
                  </div>
                </div>
              ))}
              <Link to="/landlord/maintenance" style={{ fontSize: 12, color: '#16a34a', fontWeight: 700, textDecoration: 'none' }}>Open maintenance →</Link>
            </section>

            <section className="pp-section">
              <h2 className="pp-section-title">Certificates ({detail.certificates?.length || 0})</h2>
              {(detail.certificates || []).length === 0 ? <div className="pp-empty">No certificates on file</div> : (detail.certificates || []).map((c: any) => (
                <div key={c.id} className="pp-row">
                  <div>
                    <div className="pp-row-title">{c.cert_type}</div>
                    <div className="pp-row-sub">{c.expiry_date ? `Expires ${fmt(c.expiry_date)}` : 'No expiry'}</div>
                  </div>
                  <span className={`pp-badge ${c.cert_status === 'expired' ? 'pp-badge-red' : c.cert_status === 'expiring_soon' ? 'pp-badge-amber' : 'pp-badge-green'}`}>
                    {labelize(c.cert_status)}
                  </span>
                </div>
              ))}
              <Link to="/landlord/certificates" style={{ fontSize: 12, color: '#16a34a', fontWeight: 700, textDecoration: 'none' }}>Manage certificates →</Link>
            </section>

            <section className="pp-section">
              <h2 className="pp-section-title">Viewings ({detail.viewings?.length || 0})</h2>
              {(detail.viewings || []).length === 0 ? <div className="pp-empty">No viewings scheduled</div> : (detail.viewings || []).slice(0, 4).map((v: any) => (
                <div key={v.id} className="pp-row">
                  <div>
                    <div className="pp-row-title">{v.visitor_name} — {labelize(v.type)}</div>
                    <div className="pp-row-sub">{new Date(v.date_time).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                </div>
              ))}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
